import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, BarChart3, Building2, Database, Download, Lock, RefreshCw, Send, ShieldCheck, Users } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import communicationService, {
  AdminBroadcastUnsubscribe,
  AdminRegisteredUserProfile,
  AdminUserWorkspaceView,
  AdminWorkspaceSnapshot,
  BroadcastFailure,
  BroadcastHistoryRecord,
  BroadcastRecipient,
  isOwnerEmail
} from '../../services/CommunicationService';
import teamWorkspaceService, { PlatformUsageRecord } from '../../services/TeamWorkspaceService';
import { downloadTextFile } from '../../utils/dataExchange';

const normalizeEmail = (email?: string) => (email || '').trim().toLowerCase();
const getAccountIdentifier = (user: BroadcastRecipient) => user.email || user.phoneNumber || user.uid || 'Unknown Firebase account';

const isValidRecipient = (user: BroadcastRecipient) =>
  Boolean(user.email && user.email.includes('@') && !user.disabled);

const formatAdminDate = (value?: string) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not recorded' : date.toLocaleString();
};

const getSnapshotCounts = (snapshot?: AdminWorkspaceSnapshot | null) => ({
  trucks: snapshot?.appState?.trucks?.length || 0,
  maintenance: snapshot?.appState?.maintenance?.length || 0,
  parts: snapshot?.appState?.parts?.length || 0,
  inspections: snapshot?.fleetOpsWorkspace?.inspections?.length || 0,
  issues: snapshot?.fleetOpsWorkspace?.issueReports?.length || 0,
  workOrders: snapshot?.fleetOpsWorkspace?.workOrders?.length || 0,
  compliance: snapshot?.fleetOpsWorkspace?.complianceDocuments?.length || 0,
  teamMembers: snapshot?.fleetOpsWorkspace?.teamMembers?.length || 0
});

const buildLocalWorkspaceActivity = (
  user: BroadcastRecipient,
  snapshot: AdminWorkspaceSnapshot,
  includeAllWorkspaceActivity: boolean
) => {
  const identities = new Set([user.uid, user.email, user.name].map(value => normalizeEmail(value)).filter(Boolean));
  const matchesUser = (value?: string) => identities.has(normalizeEmail(value));
  const activity: any[] = [];
  const addActivity = (record: any, action: string, entity: string, actorField: string, timestampFields = ['updatedAt', 'createdAt', 'date']) => {
    if (!includeAllWorkspaceActivity && !matchesUser(record?.[actorField])) return;
    activity.push({
      id: `${entity}-${record?.id || activity.length}-${action}`,
      action,
      entity,
      entityId: record?.id || '',
      detail: getRecordLabel(record, `${entity} record`),
      actor: record?.[actorField] || user.email || user.name || user.uid,
      createdAt: timestampFields.map(field => record?.[field]).find(Boolean) || ''
    });
  };

  (snapshot.fleetOpsWorkspace?.auditEvents || []).forEach(event => {
    if (includeAllWorkspaceActivity || matchesUser(event.actor)) activity.push(event);
  });
  (snapshot.appState?.trucks || []).forEach(record => {
    addActivity(record, 'Created truck', 'truck', 'createdBy', ['createdAt']);
    addActivity(record, 'Updated truck', 'truck', 'updatedBy', ['updatedAt']);
  });
  (snapshot.appState?.maintenance || []).forEach(record => {
    addActivity(record, 'Created maintenance record', 'maintenance', 'createdBy', ['createdAt', 'date']);
    addActivity(record, 'Updated maintenance record', 'maintenance', 'updatedBy', ['updatedAt', 'date']);
  });
  (snapshot.appState?.parts || []).forEach(record => {
    addActivity(record, 'Created part', 'part', 'createdBy', ['createdAt']);
    addActivity(record, 'Updated part', 'part', 'updatedBy', ['updatedAt']);
  });
  (snapshot.fleetOpsWorkspace?.inspections || []).forEach(record => addActivity(record, 'Completed inspection', 'inspection', 'inspectorName'));
  (snapshot.fleetOpsWorkspace?.issueReports || []).forEach(record => addActivity(record, 'Reported issue', 'issue report', 'reportedBy'));
  (snapshot.fleetOpsWorkspace?.workOrders || []).forEach(record => {
    addActivity(record, 'Created work order', 'work order', 'requestedBy');
    addActivity(record, 'Assigned work order', 'work order', 'assignedTo');
  });
  (snapshot.fleetOpsWorkspace?.complianceDocuments || []).forEach(record => addActivity(record, 'Uploaded compliance record', 'compliance', 'owner'));

  return activity.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
};

const createFallbackUserWorkspaceView = (
  user: BroadcastRecipient,
  snapshots: AdminWorkspaceSnapshot[],
  profiles: AdminRegisteredUserProfile[]
): AdminUserWorkspaceView => {
  const email = normalizeEmail(user.email);
  const profile = profiles.find(item => item.id === user.uid || normalizeEmail(item.email) === email);
  const snapshot = snapshots.find(item => (
    item.scopeId === profile?.companyId ||
    item.scopeId === profile?.id ||
    item.scopeId === user.uid ||
    (email && normalizeEmail(item.ownerEmail) === email) ||
    (item.users || []).some(member => member.id === user.uid || (email && normalizeEmail(member.email) === email))
  ));
  const emptySnapshot: AdminWorkspaceSnapshot = {
    scopeId: profile?.companyId || profile?.id || user.uid || email,
    ownerEmail: email,
    users: [{ id: user.uid, email, name: user.name, role: profile?.role || user.role || 'user' }],
    appState: { trucks: [], maintenance: [], parts: [] },
    fleetOpsWorkspace: {
      inspections: [],
      issueReports: [],
      workOrders: [],
      complianceDocuments: [],
      telematicsEvents: [],
      teamMembers: [],
      auditEvents: []
    },
    updatedAt: ''
  };
  const resolvedSnapshot = snapshot || emptySnapshot;
  const shared = Boolean(snapshot && resolvedSnapshot.scopeId !== user.uid && (
    profile?.companyId === resolvedSnapshot.scopeId || (resolvedSnapshot.users || []).length > 1
  ));
  const includeAllWorkspaceActivity = Boolean(snapshot && !shared);
  const matchingAuditEvents = (resolvedSnapshot.fleetOpsWorkspace?.auditEvents || []).filter((event: any) => {
    const actor = normalizeEmail(event.actor);
    return includeAllWorkspaceActivity || (email && actor === email) || actor === normalizeEmail(user.name);
  });
  const activity = buildLocalWorkspaceActivity(user, resolvedSnapshot, includeAllWorkspaceActivity);

  return {
    userId: user.uid || email,
    account: user,
    profile: profile || null,
    workspaceScopeId: resolvedSnapshot.scopeId,
    workspaceStatus: snapshot ? (shared ? 'shared' : 'persisted') : 'not-initialized',
    workspaceExists: Boolean(snapshot),
    matchingAuditEvents,
    activity,
    snapshot: resolvedSnapshot
  };
};

const getRecordLabel = (record: any, fallback: string) => (
  record?.title ||
  record?.nickname ||
  record?.name ||
  record?.type ||
  record?.category ||
  record?.action ||
  record?.email ||
  record?.licensePlate ||
  record?.description ||
  fallback
);

const stringifyAdminRecord = (record: any) => JSON.stringify(record, (key, value) => {
  if (typeof value === 'string' && value.length > 1000) {
    return `[Large attachment data hidden in panel: ${value.length.toLocaleString()} characters. Export the workspace for the full value.]`;
  }
  return value;
}, 2);

const WorkspaceRecordList: React.FC<{ title: string; records?: any[]; emptyText: string }> = ({ title, records = [], emptyText }) => (
  <section className="owner-workspace-record-section">
    <div className="owner-workspace-record-heading">
      <strong>{title}</strong>
      <span>{records.length}</span>
    </div>
    {records.length === 0 ? (
      <p>{emptyText}</p>
    ) : (
      <div className="owner-workspace-record-list">
        {records.map((record, index) => (
          <details key={record?.id || record?.email || `${title}-${index}`}>
            <summary>
              <strong>{getRecordLabel(record, `${title} record ${index + 1}`)}</strong>
              <span>{record?.status || record?.role || record?.severity || record?.createdAt || record?.updatedAt || ''}</span>
            </summary>
            <pre>{stringifyAdminRecord(record)}</pre>
          </details>
        ))}
      </div>
    )}
  </section>
);

const AdminPanel: React.FC = () => {
  const { state } = useAppContext();
  const navigate = useNavigate();
  const isOwner = isOwnerEmail(state.currentUser?.email);
  const isWorkspaceAdmin = state.currentUser?.role === 'admin';
  const [auditMessage, setAuditMessage] = useState<string>('System controls are ready.');
  const [usageRecords, setUsageRecords] = useState<PlatformUsageRecord[]>([]);
  const [broadcastForm, setBroadcastForm] = useState({ subject: '', message: '' });
  const [broadcastStatus, setBroadcastStatus] = useState<string>('Owner broadcast tools are ready.');
  const [broadcastFailures, setBroadcastFailures] = useState<BroadcastFailure[]>([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [authUsers, setAuthUsers] = useState<BroadcastRecipient[]>([]);
  const [registeredProfiles, setRegisteredProfiles] = useState<AdminRegisteredUserProfile[]>([]);
  const [broadcastUnsubscribes, setBroadcastUnsubscribes] = useState<AdminBroadcastUnsubscribe[]>([]);
  const [workspaceSnapshots, setWorkspaceSnapshots] = useState<AdminWorkspaceSnapshot[]>([]);
  const [userWorkspaceViews, setUserWorkspaceViews] = useState<AdminUserWorkspaceView[]>([]);
  const [broadcastHistory, setBroadcastHistory] = useState<BroadcastHistoryRecord[]>([]);
  const [selectedRecipientEmails, setSelectedRecipientEmails] = useState<Set<string>>(new Set());
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [authUsersStatus, setAuthUsersStatus] = useState<string>('Firebase Auth roster has not been loaded yet.');
  const [loadingAuthUsers, setLoadingAuthUsers] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    void teamWorkspaceService.listUsageRecords().then(records => {
      if (!cancelled) setUsageRecords(records);
    });
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  const loadAuthUsers = async () => {
    if (!state.currentUser || !isOwner) return;
    const ownerEmail = normalizeEmail(state.currentUser.email);
    const ownerRecipient: BroadcastRecipient = {
      uid: state.currentUser.id,
      email: ownerEmail,
      name: state.currentUser.name || 'FleetFix Owner',
      role: 'owner',
      status: 'owner',
      emailVerified: true,
      disabled: false
    };
    const includeOwner = (users: BroadcastRecipient[]) => (
      ownerEmail && !users.some(user => normalizeEmail(user.email) === ownerEmail)
        ? [ownerRecipient, ...users]
        : users
    );
    setLoadingAuthUsers(true);
    setAuthUsersStatus('Loading owner directory from Firebase Auth and workspace snapshots...');
    try {
      const directory = await communicationService.listOwnerDirectoryData(state.currentUser.email);
      const usersWithOwner = includeOwner(directory.users);
      setAuthUsers(usersWithOwner);
      setRegisteredProfiles(directory.registeredUsers);
      setBroadcastUnsubscribes(directory.unsubscribes);
      setWorkspaceSnapshots(directory.workspaceSnapshots);
      const viewsByUserId = new Map(directory.userWorkspaceViews.map(view => [view.userId, view]));
      const completeUserViews = usersWithOwner.map(user => (
        viewsByUserId.get(user.uid || '') ||
        createFallbackUserWorkspaceView(user, directory.workspaceSnapshots, directory.registeredUsers)
      ));
      setUserWorkspaceViews(completeUserViews);
      setBroadcastHistory(directory.broadcastHistory);
      const selectableUsers = usersWithOwner.filter(isValidRecipient);
      const verifiedSelection = selectableUsers
        .filter(user => user.emailVerified)
        .map(user => normalizeEmail(user.email));
      const defaultSelection = verifiedSelection.length > 0
        ? verifiedSelection
        : selectableUsers.map(user => normalizeEmail(user.email));
      setSelectedRecipientEmails(current => {
        const available = new Set(selectableUsers.map(user => normalizeEmail(user.email)));
        const retained = Array.from(current).filter(email => available.has(email));
        return new Set(retained.length > 0 ? retained : defaultSelection);
      });
      setSelectedWorkspaceId(current => current || directory.workspaceSnapshots[0]?.scopeId || '');
      setSelectedUserId(current => current || completeUserViews[0]?.userId || '');
      setAuthUsersStatus(`${usersWithOwner.length} registered accounts, ${completeUserViews.length} user workspace views, and ${directory.workspaceSnapshots.length} persisted workspaces loaded.`);
    } catch (error) {
      const fallbackUsers = ownerEmail ? [ownerRecipient] : [];
      setAuthUsers(fallbackUsers);
      setRegisteredProfiles([]);
      setBroadcastUnsubscribes([]);
      setWorkspaceSnapshots([]);
      const fallbackViews = fallbackUsers.map(user => createFallbackUserWorkspaceView(user, [], []));
      setUserWorkspaceViews(fallbackViews);
      setSelectedUserId(fallbackViews[0]?.userId || '');
      setSelectedRecipientEmails(new Set(fallbackUsers.map(user => normalizeEmail(user.email))));
      setAuthUsersStatus(`${error instanceof Error ? error.message : 'Unable to load Firebase Auth users.'} Owner email is still available for owner-only broadcast testing.`);
    } finally {
      setLoadingAuthUsers(false);
    }
  };

  useEffect(() => {
    if (!isOwner || !state.currentUser) return;
    void loadAuthUsers();
    // loadAuthUsers intentionally reads the current owner session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, state.currentUser?.email]);

  const adminStats = useMemo(() => {
    const openMaintenance = state.maintenance.filter((entry: any) => entry.status !== 'completed').length;
    const partsBelowMinimum = state.parts.filter((part: any) => Number(part.quantity || part.inventoryLevel || 0) <= Number(part.minimumStock || part.reorderPoint || 0)).length;
    const inactiveVehicles = state.trucks.filter((truck: any) => truck.status !== 'In Service').length;

    return {
      vehicles: state.trucks.length,
      openMaintenance,
      partsBelowMinimum,
      inactiveVehicles,
      platformUsers: authUsers.length || usageRecords.reduce((sum, record) => sum + record.users.length, 0),
      platformVehicles: workspaceSnapshots.reduce((sum, snapshot) => sum + getSnapshotCounts(snapshot).trucks, 0) || usageRecords.reduce((sum, record) => sum + record.trucks, 0)
    };
  }, [state.maintenance, state.parts, state.trucks, usageRecords, authUsers, workspaceSnapshots]);

  const availableBroadcastUsers = useMemo(() => authUsers.filter(isValidRecipient), [authUsers]);

  const broadcastRecipients = useMemo(() => (
    availableBroadcastUsers
      .filter(user => selectedRecipientEmails.has(normalizeEmail(user.email)))
      .map(user => ({
        uid: user.uid,
        email: normalizeEmail(user.email),
        name: user.name,
        role: user.role,
        status: user.emailVerified ? 'verified' : 'registered',
        emailVerified: user.emailVerified,
        disabled: user.disabled
      }))
  ), [availableBroadcastUsers, selectedRecipientEmails]);

  const snapshotByScope = useMemo(() => {
    const map = new Map<string, AdminWorkspaceSnapshot>();
    workspaceSnapshots.forEach(snapshot => map.set(snapshot.scopeId || snapshot.id || '', snapshot));
    return map;
  }, [workspaceSnapshots]);

  const workspaceForEmail = (email?: string) => {
    const normalizedEmail = normalizeEmail(email);
    return workspaceSnapshots.find(snapshot =>
      normalizeEmail(snapshot.ownerEmail) === normalizedEmail ||
      (snapshot.users || []).some(user => normalizeEmail(user.email) === normalizedEmail)
    );
  };

  const workspaceForProfile = (profile: Pick<AdminRegisteredUserProfile, 'email' | 'companyId' | 'id'>) => (
    (profile.companyId ? snapshotByScope.get(profile.companyId) : undefined) ||
    (profile.id ? snapshotByScope.get(profile.id) : undefined) ||
    workspaceForEmail(profile.email)
  );

  const selectedUserView = selectedUserId
    ? userWorkspaceViews.find(view => view.userId === selectedUserId)
    : undefined;
  const selectedWorkspace = selectedUserView?.snapshot || (selectedWorkspaceId ? snapshotByScope.get(selectedWorkspaceId) : undefined);
  const selectedWorkspaceCounts = getSnapshotCounts(selectedWorkspace);

  const selectAllRecipients = () => setSelectedRecipientEmails(new Set(availableBroadcastUsers.map(user => normalizeEmail(user.email))));
  const selectVerifiedRecipients = () => {
    const verified = availableBroadcastUsers.filter(user => user.emailVerified).map(user => normalizeEmail(user.email));
    setSelectedRecipientEmails(new Set(verified.length ? verified : availableBroadcastUsers.map(user => normalizeEmail(user.email))));
  };
  const clearSelectedRecipients = () => setSelectedRecipientEmails(new Set());
  const toggleRecipient = (email: string) => {
    const normalizedEmail = normalizeEmail(email);
    setSelectedRecipientEmails(current => {
      const next = new Set(current);
      if (next.has(normalizedEmail)) {
        next.delete(normalizedEmail);
      } else {
        next.add(normalizedEmail);
      }
      return next;
    });
  };

  const buildAuditReport = () => {
    const issues = [
      adminStats.inactiveVehicles ? `${adminStats.inactiveVehicles} vehicle${adminStats.inactiveVehicles === 1 ? '' : 's'} not in service` : '',
      adminStats.openMaintenance ? `${adminStats.openMaintenance} open maintenance record${adminStats.openMaintenance === 1 ? '' : 's'}` : '',
      adminStats.partsBelowMinimum ? `${adminStats.partsBelowMinimum} part${adminStats.partsBelowMinimum === 1 ? '' : 's'} below minimum stock` : '',
      isOwner && availableBroadcastUsers.length === 0 ? 'no enabled broadcast recipients loaded' : '',
      isOwner && userWorkspaceViews.length !== authUsers.length ? `${authUsers.length - userWorkspaceViews.length} registered account views missing` : '',
      isOwner && workspaceSnapshots.length === 0 ? 'no persisted remote workspaces loaded' : ''
    ].filter(Boolean);

    return {
      generatedAt: new Date().toISOString(),
      status: issues.length ? 'attention' : 'clear',
      issues,
      checks: {
        accountIsolation: 'enabled',
        ownerOnlyTools: isOwner ? 'visible' : 'hidden',
        selectedBroadcastRecipients: broadcastRecipients.length,
        eligibleBroadcastRecipients: availableBroadcastUsers.length,
        registeredUsers: authUsers.length,
        userWorkspaceViews: userWorkspaceViews.length,
        workspaceSnapshots: workspaceSnapshots.length,
        workspaceVehicles: adminStats.platformVehicles || state.trucks.length,
        currentWorkspaceVehicles: state.trucks.length,
        currentWorkspaceOpenMaintenance: adminStats.openMaintenance,
        currentWorkspaceLowStockParts: adminStats.partsBelowMinimum
      }
    };
  };

  const exportAdminSnapshot = () => {
    const auditReport = buildAuditReport();
    const filename = `fleetfix-admin-snapshot-${new Date().toISOString().slice(0, 10)}.json`;
    const payload = {
      exportedAt: new Date().toISOString(),
      auditReport,
      admin: state.currentUser ? {
        id: state.currentUser.id,
        email: state.currentUser.email,
        role: state.currentUser.role,
        companyId: state.currentUser.companyId
      } : null,
      summary: adminStats,
      usageRecords,
      firebaseAuthUsers: authUsers,
      registeredProfiles,
      broadcastUnsubscribes,
      workspaceSnapshots,
      userWorkspaceViews,
      broadcastHistory,
      trucks: state.trucks,
      maintenance: state.maintenance,
      parts: state.parts
    };
    downloadTextFile(filename, JSON.stringify(payload, null, 2), 'application/json');
    setAuditMessage(`Snapshot exported: ${filename}. Included ${authUsers.length} registered users, ${userWorkspaceViews.length} user workspace views, ${workspaceSnapshots.length} persisted workspaces, ${state.trucks.length} trucks, ${state.maintenance.length} maintenance records, and ${state.parts.length} parts.`);
  };

  const runHealthAudit = () => {
    const report = buildAuditReport();
    const timestamp = new Date(report.generatedAt).toLocaleString();
    const healthySummary = `${report.checks.currentWorkspaceVehicles} vehicles, ${report.checks.selectedBroadcastRecipients}/${report.checks.eligibleBroadcastRecipients} broadcast recipients selected, ${report.checks.userWorkspaceViews}/${report.checks.registeredUsers} account views ready, ${report.checks.workspaceSnapshots} persisted workspaces loaded`;
    setAuditMessage(report.issues.length ? `Audit complete (${timestamp}): ${report.issues.join(', ')}.` : `Audit complete (${timestamp}): no fleet exceptions found. ${healthySummary}.`);
  };

  const sendBroadcast = async (event: FormEvent) => {
    event.preventDefault();
    if (!state.currentUser || !isOwner) return;
    if (!broadcastForm.subject.trim() || !broadcastForm.message.trim()) {
      setBroadcastStatus('Add a subject and message before sending.');
      return;
    }
    if (broadcastRecipients.length === 0) {
      setBroadcastStatus('No registered recipients are available yet.');
      return;
    }

    setBroadcasting(true);
    setBroadcastFailures([]);
    setBroadcastStatus('Sending broadcast email...');
    try {
      const result = await communicationService.sendOwnerBroadcast({
        ownerEmail: state.currentUser.email,
        subject: broadcastForm.subject,
        message: broadcastForm.message,
        recipients: broadcastRecipients
      });
      setBroadcastFailures(result.failures || []);
      if (result.broadcastLog) {
        setBroadcastHistory(current => [result.broadcastLog as BroadcastHistoryRecord, ...current.filter(item => item.id !== result.broadcastLog?.id)].slice(0, 50));
      }
      setBroadcastStatus(`Broadcast complete: ${result.sent} sent, ${result.failed} failed.`);
      setBroadcastForm({ subject: '', message: '' });
    } catch (error) {
      setBroadcastStatus(error instanceof Error ? error.message : 'Broadcast failed.');
    } finally {
      setBroadcasting(false);
    }
  };

  if (!isWorkspaceAdmin && !isOwner) {
    return (
      <div className="admin-panel page-container">
        <div className="page-header">
          <div className="header-content">
            <h1>Admin Console</h1>
            <p>Protected workspace controls for owners and administrators.</p>
          </div>
        </div>

        <div className="card admin-access-card">
          <div className="card-body">
            <Lock size={36} />
            <h2>Admin Access Required</h2>
            <p>Your current role is {state.currentUser?.role || 'unknown'}. Company controls stay hidden unless an administrator grants access.</p>
            <button className="btn btn-primary" type="button" onClick={() => navigate('/company')}>
              <Users size={16} />
              View Company Access
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel page-container">
      <div className="page-header">
        <div className="header-content">
          <h1>{isOwner ? 'Owner Command Console' : 'Admin Console'}</h1>
          <p>{isOwner ? 'Owner-only usage visibility, user communication, audits, and workspace controls.' : 'Manage workspace health, audit exports, data posture, and enterprise controls from one place.'}</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" type="button" onClick={runHealthAudit}>
            <Activity size={16} />
            Run Audit
          </button>
          <button className="btn-primary" type="button" onClick={exportAdminSnapshot}>
            <Download size={16} />
            Export Snapshot
          </button>
        </div>
      </div>

      <div className="ops-metric-grid">
        <div className="ops-metric-card">
          <span className="ops-metric-icon"><Building2 size={18} /></span>
          <div><strong>{adminStats.vehicles}</strong><span>Workspace Vehicles</span></div>
        </div>
        <div className={`ops-metric-card ${adminStats.openMaintenance ? 'warning' : 'success'}`}>
          <span className="ops-metric-icon"><ShieldCheck size={18} /></span>
          <div><strong>{adminStats.openMaintenance}</strong><span>Open Maintenance</span></div>
        </div>
        <div className={`ops-metric-card ${adminStats.partsBelowMinimum ? 'danger' : 'success'}`}>
          <span className="ops-metric-icon"><Database size={18} /></span>
          <div><strong>{adminStats.partsBelowMinimum}</strong><span>Low Stock Parts</span></div>
        </div>
        <div className="ops-metric-card">
          <span className="ops-metric-icon"><BarChart3 size={18} /></span>
          <div><strong>{isOwner ? adminStats.platformUsers : adminStats.inactiveVehicles}</strong><span>{isOwner ? 'Registered Users' : 'Fleet Exceptions'}</span></div>
        </div>
      </div>

      {isOwner && (
        <div className="ops-grid two-col owner-console-grid">
          <section className="ops-panel">
            <div className="ops-panel-header">
              <h2>Owner Broadcast</h2>
              <span className="status-pill success">{broadcastRecipients.length} selected</span>
            </div>
            <form onSubmit={sendBroadcast} className="owner-broadcast-form">
              <label>Subject<input value={broadcastForm.subject} onChange={event => setBroadcastForm(form => ({ ...form, subject: event.target.value }))} placeholder="Product update, announcement, or promotion" /></label>
              <label>Message<textarea value={broadcastForm.message} onChange={event => setBroadcastForm(form => ({ ...form, message: event.target.value }))} rows={7} placeholder="Write the email message users will receive." /></label>
              <p className="ops-muted">{broadcastStatus}</p>
              {broadcastFailures.length > 0 && (
                <div className="broadcast-failure-list">
                  {broadcastFailures.slice(0, 8).map(failure => (
                    <span key={`${failure.email}-${failure.reason || failure.error}`}>
                      {failure.email}: {failure.reason || failure.error || 'Provider rejected this recipient'}
                    </span>
                  ))}
                </div>
              )}
              <div className="broadcast-recipient-toolbar">
                <button type="button" className="btn-secondary small" onClick={selectAllRecipients}>All active</button>
                <button type="button" className="btn-secondary small" onClick={selectVerifiedRecipients}>Verified only</button>
                <button type="button" className="btn-secondary small" onClick={clearSelectedRecipients}>None</button>
                <span>{broadcastRecipients.length} of {availableBroadcastUsers.length} eligible users selected · {broadcastUnsubscribes.length} unsubscribed</span>
              </div>
              <div className="broadcast-recipient-list">
                {availableBroadcastUsers.length === 0 ? (
                  <div className="ops-empty-state"><Users size={20} /><strong>No eligible recipients</strong><p>Enabled Firebase Auth users appear here after they register.</p></div>
                ) : availableBroadcastUsers.slice(0, 120).map(user => {
                  const email = normalizeEmail(user.email);
                  return (
                    <label className="broadcast-recipient-row" key={user.uid || email}>
                      <input type="checkbox" checked={selectedRecipientEmails.has(email)} onChange={() => toggleRecipient(email)} />
                      <span>
                        <strong>{user.name || email}</strong>
                        <small>{email}</small>
                      </span>
                      <em className={`status-pill ${user.emailVerified ? 'success' : 'pending'}`}>{user.emailVerified ? 'Verified' : 'Unverified'}</em>
                    </label>
                  );
                })}
              </div>
              <p className="ops-muted">Broadcasts only go to the selected enabled Firebase Auth accounts.</p>
              {broadcastUnsubscribes.length > 0 && (
                <div className="broadcast-unsubscribe-note">
                  <strong>Unsubscribed users skipped</strong>
                  <span>{broadcastUnsubscribes.slice(0, 6).map(item => item.email).join(', ')}</span>
                </div>
              )}
              <button className="btn-primary" type="submit" disabled={broadcasting}>
                <Send size={16} />
                {broadcasting ? 'Sending' : 'Send Broadcast'}
              </button>
            </form>
          </section>

          <section className="ops-panel">
            <div className="ops-panel-header">
              <h2>Firebase Registered Users</h2>
              <button className="btn-secondary compact-action" type="button" onClick={loadAuthUsers} disabled={loadingAuthUsers}>
                <RefreshCw size={15} />
                {loadingAuthUsers ? 'Loading' : 'Refresh'}
              </button>
            </div>
            <p className="ops-muted">{authUsersStatus}</p>
            <div className="owner-user-list">
              {authUsers.length === 0 ? (
                <div className="ops-empty-state"><Users size={22} /><strong>No Firebase Auth users loaded</strong><p>Registered users appear here after Firebase Admin credentials are configured in Vercel.</p></div>
              ) : authUsers.slice(0, 30).map(user => (
                <div className="owner-user-card" key={user.uid || user.email}>
                  <div>
                    <button
                      className="owner-email-link"
                      type="button"
                      onClick={() => {
                        const userView = userWorkspaceViews.find(view => view.userId === (user.uid || normalizeEmail(user.email)));
                        setSelectedUserId(userView?.userId || user.uid || normalizeEmail(user.email));
                        setSelectedWorkspaceId(userView?.workspaceScopeId || '');
                      }}
                    >
                      {user.name || getAccountIdentifier(user)}
                    </button>
                    <span>{getAccountIdentifier(user)}</span>
                  </div>
                  <div className="owner-user-meta">
                    <span className={`status-pill ${user.disabled ? 'disabled' : 'active'}`}>{user.disabled ? 'Disabled' : 'Active'}</span>
                    <span className={`status-pill ${user.emailVerified ? 'success' : 'pending'}`}>{user.emailVerified ? 'Verified' : 'Unverified'}</span>
                    <span>{user.lastSignInAt ? `Last sign-in ${new Date(user.lastSignInAt).toLocaleDateString()}` : 'No sign-in yet'}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {isOwner && (
        <section className="ops-panel owner-platform-panel">
          <div className="ops-panel-header">
            <h2>User Workspace Directory</h2>
            <span className="status-pill">{userWorkspaceViews.length} user views</span>
          </div>
          <p className="ops-muted">Every Firebase Auth account has one owner-only user workspace view. Shared-company members point to the same company workspace, while accounts that have never initialized FleetFix show an empty but selectable account view.</p>
          <div className="owner-directory-grid">
            <div className="owner-directory-list">
              <div className="owner-directory-section-title">
                <strong>Registered Firebase users</strong>
                <span>{userWorkspaceViews.length} of {authUsers.length} views</span>
              </div>
              {userWorkspaceViews.length === 0 ? (
                <div className="ops-empty-state"><Users size={22} /><strong>No registered users loaded</strong><p>Firebase Auth users will appear after the owner directory refresh succeeds.</p></div>
              ) : userWorkspaceViews.map(userView => {
                const user = userView.account;
                const counts = getSnapshotCounts(userView.snapshot);
                return (
                  <button
                    className={`owner-directory-row ${selectedUserId === userView.userId ? 'selected' : ''}`}
                    type="button"
                    key={userView.userId}
                    onClick={() => {
                      setSelectedUserId(userView.userId);
                      setSelectedWorkspaceId(userView.workspaceScopeId);
                    }}
                  >
                    <span>
                      <strong>{getAccountIdentifier(user)}</strong>
                      <small>{user.name || 'No display name'} · Created {formatAdminDate(user.createdAt)}</small>
                    </span>
                    <span className="owner-directory-row-meta">
                      <em className={`status-pill ${user.disabled ? 'disabled' : 'active'}`}>{user.disabled ? 'Disabled' : 'Active'}</em>
                      <em className={`status-pill ${userView.workspaceStatus === 'not-initialized' ? 'pending' : 'success'}`}>
                        {userView.workspaceStatus === 'shared' ? 'Shared workspace' : userView.workspaceStatus === 'persisted' ? 'Persisted workspace' : 'Not initialized'}
                      </em>
                      <em>{counts.trucks} trucks</em>
                    </span>
                  </button>
                );
              })}

              {registeredProfiles.length > 0 && (
                <>
                  <div className="owner-directory-section-title">
                    <strong>App profile records</strong>
                    <span>{registeredProfiles.length} synced</span>
                  </div>
                  {registeredProfiles.slice(0, 80).map(profile => {
                    const workspace = workspaceForProfile(profile);
                    const counts = getSnapshotCounts(workspace);
                    return (
                      <button
                        className={`owner-directory-row ${selectedWorkspaceId && selectedWorkspaceId === workspace?.scopeId ? 'selected' : ''}`}
                        type="button"
                        key={profile.id || profile.email}
                        onClick={() => {
                          setSelectedUserId('');
                          setSelectedWorkspaceId(workspace?.scopeId || profile.companyId || profile.id || '');
                        }}
                      >
                        <span>
                          <strong>{profile.email}</strong>
                          <small>{profile.role || 'user'} · Last seen {formatAdminDate(profile.lastSeenAt || profile.updatedAt)}</small>
                        </span>
                        <span className="owner-directory-row-meta">
                          <em>{workspace ? `${counts.maintenance} maintenance` : 'No snapshot'}</em>
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <div className="owner-workspace-inspector">
              {selectedWorkspace ? (
                <>
                  <div className="owner-workspace-header">
                    <div>
                      <strong>{selectedUserView ? getAccountIdentifier(selectedUserView.account) : selectedWorkspace.ownerEmail || selectedWorkspace.scopeId}</strong>
                      <span>
                        Scope {selectedWorkspace.scopeId} · Updated {formatAdminDate(selectedWorkspace.updatedAt)}
                        {selectedUserView ? ` · ${selectedUserView.workspaceStatus.replace('-', ' ')}` : ''}
                      </span>
                    </div>
                    <button className="btn-secondary small" type="button" onClick={() => {
                      const exportPayload = selectedUserView || selectedWorkspace;
                      downloadTextFile(
                        `fleetfix-${selectedUserView ? `user-${selectedUserView.userId}` : `workspace-${selectedWorkspace.scopeId}`}.json`,
                        JSON.stringify(exportPayload, null, 2),
                        'application/json'
                      );
                    }}>Export Full Record</button>
                  </div>

                  {selectedUserView && (
                    <div className="owner-account-detail-grid">
                      <span>Account identifier<strong>{getAccountIdentifier(selectedUserView.account)}</strong></span>
                      <span>Firebase UID<strong>{selectedUserView.userId}</strong></span>
                      <span>Account status<strong>{selectedUserView.account.disabled ? 'Disabled' : 'Active'}</strong></span>
                      <span>Email status<strong>{selectedUserView.account.emailVerified ? 'Verified' : 'Unverified'}</strong></span>
                      <span>Created<strong>{formatAdminDate(selectedUserView.account.createdAt)}</strong></span>
                      <span>Last sign-in<strong>{formatAdminDate(selectedUserView.account.lastSignInAt)}</strong></span>
                      <span>App role<strong>{selectedUserView.profile?.role || selectedUserView.account.role || 'Not recorded'}</strong></span>
                      <span>Workspace mapping<strong>{selectedUserView.workspaceStatus === 'shared' ? 'Shared company workspace' : selectedUserView.workspaceStatus === 'persisted' ? 'Personal persisted workspace' : 'Not initialized yet'}</strong></span>
                    </div>
                  )}

                  <div className="owner-workspace-kpis">
                    <span>{selectedWorkspaceCounts.trucks} trucks</span>
                    <span>{selectedWorkspaceCounts.maintenance} maintenance</span>
                    <span>{selectedWorkspaceCounts.parts} parts</span>
                    <span>{selectedWorkspaceCounts.inspections} inspections</span>
                    <span>{selectedWorkspaceCounts.issues} issues</span>
                    <span>{selectedWorkspaceCounts.workOrders} work orders</span>
                    <span>{selectedWorkspaceCounts.compliance} compliance</span>
                    <span>{selectedWorkspaceCounts.teamMembers} users</span>
                  </div>

                  {selectedUserView?.workspaceStatus === 'not-initialized' && (
                    <div className="owner-workspace-notice">
                      This Firebase account exists, but it has not initialized or synced a FleetFix workspace yet. The account remains selectable and will populate automatically after its first successful app sync.
                    </div>
                  )}

                  <div className="owner-workspace-record-grid">
                    {selectedUserView && (
                      <WorkspaceRecordList
                        title="Account activity"
                        records={selectedUserView.activity || selectedUserView.matchingAuditEvents}
                        emptyText="No saved actions are currently attributed directly to this account."
                      />
                    )}
                    <WorkspaceRecordList title="Workspace team" records={selectedWorkspace.users} emptyText="No users listed in this workspace snapshot." />
                    <WorkspaceRecordList title="Trucks" records={selectedWorkspace.appState?.trucks} emptyText="No trucks saved yet." />
                    <WorkspaceRecordList title="Maintenance" records={selectedWorkspace.appState?.maintenance} emptyText="No maintenance records saved yet." />
                    <WorkspaceRecordList title="Parts" records={selectedWorkspace.appState?.parts} emptyText="No parts saved yet." />
                    <WorkspaceRecordList title="Inspections" records={selectedWorkspace.fleetOpsWorkspace?.inspections} emptyText="No inspections saved yet." />
                    <WorkspaceRecordList title="Issue reports" records={selectedWorkspace.fleetOpsWorkspace?.issueReports} emptyText="No issue reports saved yet." />
                    <WorkspaceRecordList title="Work orders" records={selectedWorkspace.fleetOpsWorkspace?.workOrders} emptyText="No work orders saved yet." />
                    <WorkspaceRecordList title="Compliance records" records={selectedWorkspace.fleetOpsWorkspace?.complianceDocuments} emptyText="No compliance records saved yet." />
                    <WorkspaceRecordList title="Telematics events" records={selectedWorkspace.fleetOpsWorkspace?.telematicsEvents} emptyText="No telematics events saved yet." />
                    <WorkspaceRecordList title="Team members" records={selectedWorkspace.fleetOpsWorkspace?.teamMembers} emptyText="No team members saved yet." />
                    <WorkspaceRecordList title="Complete workspace audit history" records={selectedWorkspace.fleetOpsWorkspace?.auditEvents} emptyText="No audit events saved yet." />
                  </div>
                </>
              ) : (
                <div className="ops-empty-state">
                  <Database size={24} />
                  <strong>No workspace selected</strong>
                  <p>Select a user or app profile to inspect their saved FleetFix workspace.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {isOwner && (
        <section className="ops-panel owner-platform-panel">
          <div className="ops-panel-header">
            <h2>Broadcast History</h2>
            <span className="status-pill">{broadcastHistory.length} saved</span>
          </div>
          <div className="broadcast-history-list">
            {broadcastHistory.length === 0 ? (
              <div className="ops-empty-state"><Send size={22} /><strong>No saved broadcasts yet</strong><p>Broadcast results are stored after each campaign so they stay visible across devices.</p></div>
            ) : broadcastHistory.slice(0, 12).map(record => (
              <div className="broadcast-history-row" key={record.id}>
                <div>
                  <strong>{record.deliverableSubject || record.subject}</strong>
                  <span>{formatAdminDate(record.createdAt)} · {record.requested} selected{record.campaignCode ? ` · ${record.campaignCode}` : ''}</span>
                  {record.messagePreview && <small>{record.messagePreview}</small>}
                </div>
                <div className="broadcast-history-stats">
                  <span className="status-pill success">{record.sent} sent</span>
                  <span className={`status-pill ${record.failed ? 'danger' : 'success'}`}>{record.failed} failed</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="ops-grid two-col">
        <section className="ops-panel">
          <div className="ops-panel-header">
            <h2>Enterprise Controls</h2>
            <span className="status-pill success">Operational</span>
          </div>
          <div className="admin-control-list">
            <button type="button" onClick={() => navigate('/company')}>Team roles and invite governance</button>
            <button type="button" onClick={() => navigate('/reports')}>Executive reporting and cost visibility</button>
            <button type="button" onClick={() => navigate('/compliance')}>Compliance renewals and audit trail</button>
            <button type="button" onClick={() => navigate('/integrations')}>Telematics and push integrations</button>
          </div>
        </section>

        <section className="ops-panel">
          <div className="ops-panel-header">
            <h2>Audit Status</h2>
            <span className="status-pill">Workspace</span>
          </div>
          <p className="ops-muted">{auditMessage}</p>
          <div className="admin-audit-grid">
            <span>Account isolation</span><strong>Enabled</strong>
            <span>Owner-only tools</span><strong>{isOwner ? 'Visible' : 'Hidden'}</strong>
            <span>Production email sender</span><strong>Verified domain ready</strong>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminPanel;
